/**
 * Expands stop-visit activities that contain multiple nested passenger legs (PICK / DROP / PICK/DROP)
 * into one synthetic activity per leg for CSV / manifest export only.
 *
 * This ensures resolveActivityTripId runs on a single trip scope so Trip ID matches the leg row.
 */

import type { ActivityTrip, SimulationActivity } from '../../types/simulation';

const PASSENGER_ACTIVITY_TYPES = new Set(['PICK', 'DROP', 'PICK/DROP']);

/** Top-level fields that describe the whole stop; must not leak into single-leg clones. */
const AGGREGATE_FIELDS_TO_OMIT: (keyof SimulationActivity | string)[] = [
  'number_of_rides_picked_up',
  'number_of_rides_dropped_off',
  'users_picked_up',
  'users_dropped_off',
  'standard_passengers',
  'wheelchair_passengers',
  'passengers_count',
  'number_of_passengers',
  'pick_count',
  'pickup_count',
  'drop_count',
  'dropoff_count',
  'pickup_passengers_count',
  'picked_up_passengers_count',
  'passengers_picked_up_count',
  'number_of_passengers_picked_up',
  'dropoff_passengers_count',
  'passengers_dropped_off_count',
  'number_of_passengers_dropped_off',
  'ride_id',
  'request_id',
  'rider_ride_id',
  'driver_ride_id',
  'external_reference_id',
  'pax_on_board',
  'passengers_on_board',
  'current_passengers',
  'onboard_passengers',
  'occupancy',
  'passenger_load',
];

function tripActivityUpper(trip: Record<string, unknown>): string {
  return (trip.activity_type ?? '').toString().trim().toUpperCase();
}

/**
 * Returns nested trips that are passenger legs (PICK, DROP, or PICK/DROP), in array order.
 */
export function extractPassengerLegsFromTrips(trips: unknown): Record<string, unknown>[] {
  if (!Array.isArray(trips)) return [];
  const legs: Record<string, unknown>[] = [];
  for (const t of trips) {
    if (!t || typeof t !== 'object') continue;
    const tr = t as Record<string, unknown>;
    if (PASSENGER_ACTIVITY_TYPES.has(tripActivityUpper(tr))) {
      legs.push(tr);
    }
  }
  return legs;
}

function deepCloneTrip(leg: Record<string, unknown>): ActivityTrip {
  return JSON.parse(JSON.stringify(leg)) as ActivityTrip;
}

/**
 * Build a single-leg synthetic activity from a parent stop-visit activity and
 * one of its nested passenger legs. Strips aggregate fields so per-leg
 * inference (Trip ID, pax counts, address resolution) sees only the relevant
 * leg. Exported so the vehicle detail panel can fan a multi-passenger
 * pickup/dropoff into one row per passenger using the same logic the export
 * uses.
 */
export function buildSyntheticActivityForLeg(
  parent: SimulationActivity,
  leg: Record<string, unknown>
): SimulationActivity {
  const legTyped = leg as ActivityTrip;
  const base = { ...(parent as Record<string, unknown>) } as Record<string, unknown>;

  for (const key of AGGREGATE_FIELDS_TO_OMIT) {
    delete base[key];
  }

  base.trips = [deepCloneTrip(leg)];

  const legStart =
    typeof legTyped.activity_start_time === 'string' && legTyped.activity_start_time.trim() !== ''
      ? legTyped.activity_start_time
      : undefined;
  const legEnd =
    typeof legTyped.activity_end_time === 'string' && legTyped.activity_end_time.trim() !== ''
      ? legTyped.activity_end_time
      : undefined;

  if (legStart) {
    base.activity_start_time = legStart;
    base.activity_start = legStart;
    base.start_time = legStart;
    base.time = legStart;
  }
  if (legEnd !== undefined) {
    base.activity_end_time = legEnd;
    base.activity_end = legEnd;
    base.end_time = legEnd;
  }

  return base as SimulationActivity;
}

/**
 * Duplicates stop visits with multiple nested PICK/DROP/PICK-DROP legs into one activity per leg
 * so export Trip ID and pax inference align with each leg. Single-leg and non-trip activities pass through.
 */
export function expandActivitiesForExport(activities: SimulationActivity[]): SimulationActivity[] {
  if (!activities || activities.length === 0) return [];

  const out: SimulationActivity[] = [];

  for (const activity of activities) {
    const raw = activity as Record<string, unknown>;
    const legs = extractPassengerLegsFromTrips(raw.trips);

    if (legs.length <= 1) {
      out.push(activity);
      continue;
    }

    for (const leg of legs) {
      out.push(buildSyntheticActivityForLeg(activity, leg));
    }
  }

  return out;
}
